import React, {useEffect, useRef, useState} from 'react';
import { IStorefrontQuestion} from '../../models/Storefront';
import {
    useCreateProductQuestionMutation, useDeleteProductQuestionMutation,
    useGetProductQuestionsQuery,
    useSaveProductQuestionMutation,
} from "../../store/endpoints/StorefrontQuestionEndpoints";
import StorefrontQuestionEditor from "./StorefrontQuestionEditor";
import {
    CommandBar, DefaultButton, Dialog, DialogFooter, DialogType,
    ICommandBarItemProps, IconButton, IContextualMenuProps, MessageBar, MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack
} from "@fluentui/react";
import DataGrid from "../DataGrid/DataGrid";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";

interface IStorefrontProductQuestionsFormProps {
    canEdit: boolean;
    onLoading?: (isLoading: boolean) => void;
    productId: string;
    catalog: string;
}

const StorefrontProductQuestionsForm: React.FC<IStorefrontProductQuestionsFormProps> = (props) => {
    
    const productQuestions = useGetProductQuestionsQuery({catalog: props.catalog ?? '', productId: props.productId ?? ''}, {skip: (!props.productId || props.productId === '' || !props.catalog || props.catalog === '')})
    
    const [saver] = useSaveProductQuestionMutation();
    const [deleter] = useDeleteProductQuestionMutation();
    const [createNewQuestion] = useCreateProductQuestionMutation();
    const [saving, setSaving] = useState(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [questionEditorError, setQuestionEditorError] = useState<string>();
    const [error, setError] = useState<string>();
    const [showEditor, setShowEditor] = useState(false);
    const [idToDelete, setIdToDelete] = useState<string>();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    
    const [selectedQuestion, setSelectedQuestion] = useState<IStorefrontQuestion>();


    useEffect(() => {
        return () => {
            setSelectedQuestion(undefined);
            setIdToDelete(undefined);
            setShowEditor(false);
            setSaving(false);
            setError(undefined);
        }
    }, []);

    const scrollToBottom = () => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    const getNewQuestionId = () => {
        const sequence = (productQuestions.data?.items.length ?? 0) + 1;
        
        const id = `Question-${sequence}`;
        
        if(productQuestions.data?.items.findIndex(a => a.identifier == id)){
            return crypto.randomUUID()
        }
        return id;
    }
    
    const headerCommands: ICommandBarItemProps[] = [
        {
            key: 'addQuestion',
            text: 'Add Question',
            title: 'Add Question',
            disabled: saving,
            iconProps: {iconName: 'Add'},
            onClick: () => {
                if(props.productId && props.catalog) {
                    const newQuestion = {
                        identifier: getNewQuestionId(),
                        questionText: '',
                        answers: [],
                        entityType: 'product',
                        entityId: props.productId
                    }
                    setSaving(true);
                    createNewQuestion({question: newQuestion, productId: props.productId, catalog: props.catalog})
                        .unwrap()
                        .then((r) => {
                            setSaving(false);
                            scrollToBottom()
                            setSelectedQuestion({...newQuestion, id: r.id })
                            setShowEditor(true)
                        })
                        .catch((error) => {
                            // Error: error contains the error object
                            setError(error?.data?.message ?? 'Unknown error creating question');
                            setSaving(false);
                        });
                }
            }
        }
    ]

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'saveAndClose',
                text: 'Save and Close',
                iconProps: { iconName: 'SaveAndClose' },
                onClick: () => {
                    save();
                    setSelectedQuestion(undefined);
                    setShowEditor(false);
                }
            }
        ],
    };



    const footer = () => (
        <div style={{background: "white"}}>
            <Stack horizontal tokens={{childrenGap: '1em'}}>
                <PrimaryButton split menuProps={menuProps} text='Save' onClick={() => save()} disabled={saving} />
                <DefaultButton 
                    text='Cancel' 
                    onClick={() => {
                        setSelectedQuestion(undefined)
                        setShowEditor(false)
                        setSaving(false)
                    }}
                    disabled={saving} />
            </Stack>
        </div>
    )

    const header = () => (
        <div style={{flexGrow: 1, paddingLeft: '25px', background: "white"}}>
            <h1>Edit Storefront Question</h1>
            {questionEditorError &&
                <MessageBar messageBarType={MessageBarType.error}>{questionEditorError}</MessageBar>
            }
        </div>
    )
    
    const save = () => {
        setSaving(true)
        if(props.productId && props.catalog && selectedQuestion?.id) {
            saver({question: selectedQuestion, catalog: props.catalog, productId: props.productId, questionId: selectedQuestion.id}).unwrap()
                .then((e) => {
                    if(e.success == false){
                        setQuestionEditorError(e.message ?? 'Unknown error saving question')
                    }
                    setSaving(false)
                })
                .catch((error) => {
                    // Error: error contains the error object
                    setQuestionEditorError(error?.data?.message ?? 'Unknown error creating question');
                    setSaving(false);
                });
        }
    }

    const getCustomColumns = () => {
        const columnDetails: IColumnDetails[] = [
            {
                index: 0,
                identifier: 'Details',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton data-testid='edit-product-question' iconProps={{iconName: 'edit'}} onClick={() => {
                        setSelectedQuestion(q)
                        setShowEditor(true)
                    }}/>,
                required: true,
            },
            {
                identifier: 'identifier',
                label: 'Identifier',
                type: '',
                sortable: false,
                filterable: false,
                effectiveWidth: 200,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.identifier}</div>,
            },
            {
                identifier: 'questionText',
                label: 'Question Text',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.questionText}</div>,

            },
            {
                identifier: 'required',
                label: 'Required',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.isRequired ? ('True') : ('False')}</div>,
            },
            {
                identifier: 'Delete',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton
                        data-testid='delete-product-question'
                        iconProps={{iconName: 'Delete'}}
                        disabled={!props.canEdit}
                        onClick={() => {
                        if(q.id) {
                            setIdToDelete(q.id)
                            setShowConfirmDelete(true)
                        }
                    }}/>,
                required: true,
            },
        ];
        return columnDetails
    }

    const deleteQuestion = (id: string) => {
        
        deleter({catalog: props.catalog, productId: props.productId, questionId: id})
        setIdToDelete(undefined)
    }

    return (
        <div>
            <Dialog
                hidden={!showConfirmDelete}
                onDismiss={() => setShowConfirmDelete(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Delete Question',
                    subText: 'Are you sure you want to delete this question?',
                }}
                modalProps={{
                    isBlocking: true,
                }}
            >
                <DialogFooter>
                    <DefaultButton
                        primary
                        text='Delete'
                        onClick={() => {
                            if(idToDelete) {
                                deleteQuestion(idToDelete)
                                setShowConfirmDelete(false)
                            }
                        }}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => setShowConfirmDelete(false)}
                    />
                </DialogFooter>
            </Dialog>
            <Panel
                onOuterClick={() => {console.log("swallowing modal close event")}}
                type={PanelType.large}
                headerText={'Edit Storefront Question'}
                isOpen={showEditor}
                onDismiss={() => setShowEditor(false)}
                closeButtonAriaLabel='Close'
                isFooterAtBottom={true}
                onRenderFooterContent={footer}
                onRenderHeader={header}
                styles={{
                    commands: {
                        background: "white"
                    },
                    footer: {
                        background: "white"
                    },
                    navigation: {
                        flex: '1 1 auto',
                    },
                }}
            >
                <StorefrontQuestionEditor
                    question={selectedQuestion}
                    canEdit={true}
                    onChange={(q) => {
                        setSelectedQuestion({...q})
                    }}
                />
                
            </Panel>
            {(productQuestions.isFetching || productQuestions.isLoading) ? <Spinner label={'Loading Questions...'} size={SpinnerSize.large}/> :
            <React.Fragment>
                {error &&
                    <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
                }
                {props.canEdit && <CommandBar items={headerCommands} farItems={[]}/>}
                <DataGrid
                    getItemId={(item:IStorefrontQuestion) => item.id ?? ''}
                    items={productQuestions.data?.items ?? []}
                    selectionMode={GridSelectionMode.none}
                    loading={productQuestions.isFetching || productQuestions.isLoading}
                    overrideColumns={getCustomColumns()}
                    useContentHeight={true}
                    />
            </React.Fragment>
            }
            <div ref={bottomRef} />
        </div>
    );
}

export default StorefrontProductQuestionsForm;